import React, { useState, useEffect } from 'react';
import { SERVER } from '../../../config/config';
import axios from 'axios';

const VisitsTable = () => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalVisits, setTotalVisits] = useState(0);
  const [uniqueVisitors, setUniqueVisitors] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchVisits();
  }, [currentPage]);

  const fetchVisits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${SERVER}/visits`);
      const data = response.data;
      
      setVisits(data);
      setTotalVisits(data.reduce((sum, visit) => sum + visit.visit_count, 0));
      setUniqueVisitors(data.length);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching visits:', error);
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(visits.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentVisits = visits.slice(startIndex, startIndex + itemsPerPage);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-blue-600 absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="group relative bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl p-6 text-center overflow-hidden animate-slideUp hover:scale-105 transition-transform duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center justify-center mb-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <span className="text-2xl">👥</span>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-blue-100 uppercase tracking-wider mb-2">
              Total Visits
            </h3>
            <p className="text-5xl font-bold text-white">{totalVisits}</p>
          </div>
        </div>
        
        <div className="group relative bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-xl p-6 text-center overflow-hidden animate-slideUp hover:scale-105 transition-transform duration-300" style={{ animationDelay: '0.1s' }}>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center justify-center mb-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <span className="text-2xl">🌟</span>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-purple-100 uppercase tracking-wider mb-2">
              Unique Visitors
            </h3>
            <p className="text-5xl font-bold text-white">{uniqueVisitors}</p>
          </div>
        </div>
        
        <div className="group relative bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-xl p-6 text-center overflow-hidden animate-slideUp hover:scale-105 transition-transform duration-300" style={{ animationDelay: '0.2s' }}>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="relative z-10">
            <div className="flex items-center justify-center mb-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <span className="text-2xl">📊</span>
              </div>
            </div>
            <h3 className="text-sm font-semibold text-emerald-100 uppercase tracking-wider mb-2">
              Avg Visits/Visitor
            </h3>
            <p className="text-5xl font-bold text-white">
              {uniqueVisitors > 0 ? (totalVisits / uniqueVisitors).toFixed(1) : 0}
            </p>
          </div>
        </div>
      </div>

      {/* Visitor Details Table */}
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border border-slate-200/50 animate-slideUp" style={{ animationDelay: '0.3s' }}>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-8 py-6 border-b border-slate-200/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <span className="text-white text-lg">📋</span>
            </div>
            <h2 className="text-2xl font-bold text-slate-800">Visitor Details</h2>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                  🌐 IP Address
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                  🕐 First Visit
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                  🕑 Last Visit
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                  📈 Visit Count
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {currentVisits.length > 0 ? (
                currentVisits.map((visit, index) => (
                  <tr key={visit.id} className="hover:bg-blue-50/50 transition-colors duration-200 group">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      <span className="px-3 py-1 bg-slate-100 group-hover:bg-blue-100 rounded-lg transition-colors">
                        {visit.ip_address}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {formatDate(visit.first_visit)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {formatDate(visit.last_visit)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full font-bold">
                        {visit.visit_count}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                        <span className="text-3xl">📊</span>
                      </div>
                      <p className="text-slate-500 font-medium">No visit data available</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {visits.length > itemsPerPage && (
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl font-semibold"
            >
              ← Previous
            </button>
            
            <span className="text-sm font-semibold text-slate-700 px-4 py-2 bg-white rounded-xl shadow-sm">
              Page {currentPage} of {totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl font-semibold"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitsTable;